// src/router/RequireMenuAccess.tsx
//
// FU-3 — route access uses THE SAME RULE as menu visibility.
//
// ── ONE RULE, NOW FOUR CONSUMERS ─────────────────────────────────────
// `accessCatalog` already answers "is this menu entry visible?" for the live
// sidebar, the Roles & Access editor and that editor's preview. This is the
// fourth consumer, and it asks the identical question of the identical data.
//
// That equivalence is the point, not an implementation detail. Any second
// interpretation drifts, and both directions of drift are defects we have
// already seen or reasoned about:
//
//   menu hidden, route works   → a "restriction" nothing enforces, which is
//                                how Offerings / Business Profile /
//                                Marketplace behave today
//   menu shown, route denies   → the user is sent somewhere that refuses them
//
// So: if the rail would hide the entry, the route says so plainly. If the rail
// would show it, the route renders.
//
// ── THIS IS NOT AUTHORIZATION ────────────────────────────────────────
// The CRM server remains the only authority. A guard here protects no data —
// anyone can call the API directly — and the audit that produced this work
// records, per action, where server enforcement is a capability and where it
// is still a legacy role. Nothing in this file may ever be read as enforcement,
// and no server gate may be relaxed because this exists.
//
// ── BOOT IS NOT DENIAL ───────────────────────────────────────────────
// `useCentralContext()` returns null until boot completes. The sidebar treats
// that as "hide", which is right for a rail that can appear a moment later.
// A ROUTE must not: rendering "You do not have access" and then replacing it
// with the page is worse than a spinner, and it teaches people to distrust the
// message. Unknown therefore WAITS.
import React from 'react';
import { useCentralContext } from '../services/centralContext';
import {
  buildSidebarAccessRows,
  isSidebarRowVisible,
  scopesFromCapabilities,
} from '../config/accessCatalog';
import type { MenuKey } from '../types/api';
import { AccessDenied } from '../components/common/AccessDenied';
import PageLoader from '../components/common/PageLoader';

export interface RequireMenuAccessProps {
  /** The sidebar entry this route belongs to. */
  menuKey: MenuKey;
  children: React.ReactNode;
}

export function RequireMenuAccess({
  menuKey,
  children,
}: RequireMenuAccessProps): React.ReactElement {
  const context = useCentralContext();

  // Boot not finished — wait, do not accuse. See the header.
  if (!context) return <PageLoader />;

  const granted = scopesFromCapabilities(
    (context.currentWorkspace as { capabilities?: unknown } | undefined)?.capabilities,
  );

  const row = buildSidebarAccessRows().find((r) => r.key === menuKey);

  // A route pointed at a menu key the catalog does not contain. Render it:
  // the catalog is total over SIDEBAR_ITEMS, so this means the route belongs
  // to no sidebar entry, and inventing a denial for it would be a
  // frontend-only restriction with nothing behind it. A test pins every
  // menuKey used here against the catalog so this cannot mask a typo.
  if (!row) return <>{children}</>;

  if (isSidebarRowVisible(row, granted)) return <>{children}</>;

  return <AccessDenied areaLabel={row.label} />;
}

export default RequireMenuAccess;
